import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  ArrowLeft, BookOpen, Clock, GraduationCap, Users,
  MusicNote, FileText, VideoCamera, Globe, CaretRight, PencilLine,
} from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import { CourseForm } from "./Courses";

const MATERIAL_ICON = { audio: MusicNote, document: FileText, video: VideoCamera, web: Globe };

export default function CourseDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [c, setC] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const canManage = ["super_admin", "director", "branch_manager"].includes(user?.role);

  const load = () => api.get(`/courses/${id}`).then(({ data }) => setC(data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, [id]);

  if (!c) return <div className="text-sm text-muted-foreground">Loading course...</div>;

  return (
    <div className="space-y-6">
      <Button variant="ghost" onClick={() => nav("/courses")} className="gap-2 -ml-3">
        <ArrowLeft size={16} /> Back to Catalog
      </Button>

      {/* Hero */}
      <Card className="border-border/60 overflow-hidden">
        <div className="h-40 bg-gradient-to-br from-primary via-primary/90 to-destructive/70 relative">
          <div className="absolute inset-0 flex items-center justify-between px-8">
            <div className="text-primary-foreground">
              <div className="flex gap-2 mb-3">
                <Badge className="bg-white/95 text-primary font-mono">{c.level}</Badge>
                <Badge variant="secondary" className="bg-white/20 text-white border-white/20">{c.language}</Badge>
              </div>
              <h1 className="text-3xl md:text-4xl font-bold" style={{ fontFamily: "Manrope" }}>{c.name}</h1>
              <div className="flex items-center gap-4 mt-3 text-sm opacity-90">
                <span className="flex items-center gap-1.5"><Clock size={14} /> {c.duration_weeks} weeks</span>
                <span className="flex items-center gap-1.5"><GraduationCap size={14} /> {(c.lesson_plans || []).length} lessons</span>
                <span className="flex items-center gap-1.5"><Users size={14} /> {c.enrolled_count || 0} enrolled</span>
              </div>
            </div>
            <BookOpen size={80} weight="duotone" className="text-primary-foreground opacity-25 hidden md:block" />
          </div>
        </div>
        <CardContent className="p-6 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Course price</div>
            <div className="text-3xl font-bold font-mono">${c.price}</div>
          </div>
          <div className="flex items-center gap-2">
            {canManage && (
              <Button data-testid="edit-course-btn" variant="outline"
                onClick={() => setEditOpen(true)} className="rounded-full gap-2">
                <PencilLine size={14} weight="bold" /> {t("courses.editBtn", { defaultValue: "Edit Course" })}
              </Button>
            )}
            <Button data-testid="course-enroll-btn" className="rounded-full gap-2">
              <CaretRight size={14} weight="bold" /> Enroll now
            </Button>
          </div>
        </CardContent>
      </Card>

      {canManage && (
        <Dialog open={editOpen} onOpenChange={setEditOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: "Manrope" }}>{t("courses.editCourse", { defaultValue: "Edit Course" })}</DialogTitle>
            </DialogHeader>
            <CourseForm initial={c} onSave={() => load()} onClose={() => setEditOpen(false)} />
          </DialogContent>
        </Dialog>
      )}

      <Tabs defaultValue="syllabus">
        <TabsList>
          <TabsTrigger value="syllabus" data-testid="tab-c-syllabus">Syllabus</TabsTrigger>
          <TabsTrigger value="lessons" data-testid="tab-c-lessons">Lesson Plans</TabsTrigger>
          <TabsTrigger value="books" data-testid="tab-c-books">Books</TabsTrigger>
          <TabsTrigger value="materials" data-testid="tab-c-materials">Materials</TabsTrigger>
          <TabsTrigger value="classes" data-testid="tab-c-classes">Classes</TabsTrigger>
        </TabsList>

        <TabsContent value="syllabus" className="mt-4">
          <Card className="border-border/60"><CardContent className="p-6">
            <h3 className="font-semibold mb-3" style={{ fontFamily: "Manrope" }}>Course Overview</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">{c.syllabus || c.description || "Detailed syllabus coming soon."}</p>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="lessons" className="mt-4 space-y-3">
          {(c.lesson_plans || []).length === 0 && (
            <Card className="border-border/60"><CardContent className="p-6 text-center text-muted-foreground text-sm">
              Lesson plans coming soon.
            </CardContent></Card>
          )}
          {(c.lesson_plans || []).map((l) => (
            <Card key={l.week} className="border-border/60 card-hover"><CardContent className="p-5">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-md bg-primary/10 text-primary flex items-center justify-center shrink-0">
                  <div className="text-center">
                    <div className="text-[9px] uppercase tracking-widest font-semibold">Week</div>
                    <div className="text-lg font-bold font-mono leading-none">{l.week}</div>
                  </div>
                </div>
                <div className="min-w-0 flex-1">
                  <h4 className="font-semibold" style={{ fontFamily: "Manrope" }}>{l.title}</h4>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {(l.topics || []).map((t, i) => (
                      <Badge key={i} variant="secondary" className="text-[10px]">{t}</Badge>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent></Card>
          ))}
        </TabsContent>

        <TabsContent value="books" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {(c.books || []).length === 0 && (
              <Card className="border-border/60"><CardContent className="p-6 text-center text-muted-foreground text-sm">
                No books listed yet.
              </CardContent></Card>
            )}
            {(c.books || []).map((b, i) => (
              <Card key={i} className="border-border/60 card-hover"><CardContent className="p-5 flex gap-4">
                <div className="w-14 h-20 rounded-md bg-gradient-to-br from-primary to-destructive/60 flex items-center justify-center shrink-0">
                  <BookOpen size={22} weight="duotone" className="text-primary-foreground" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-sm" style={{ fontFamily: "Manrope" }}>{b.title}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">{b.author}</div>
                  <div className="text-[10px] text-muted-foreground font-mono mt-2">ISBN: {b.isbn}</div>
                </div>
              </CardContent></Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="materials" className="mt-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {(c.materials || []).length === 0 && (
              <Card className="border-border/60 md:col-span-4"><CardContent className="p-6 text-center text-muted-foreground text-sm">
                Learning materials coming soon.
              </CardContent></Card>
            )}
            {(c.materials || []).map((m, i) => {
              const Icon = MATERIAL_ICON[m.type] || FileText;
              return (
                <Card key={i} className="border-border/60 card-hover"><CardContent className="p-5 text-center">
                  <div className={cn("w-12 h-12 mx-auto rounded-md flex items-center justify-center",
                    m.type === "audio" && "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
                    m.type === "video" && "bg-destructive/10 text-destructive",
                    m.type === "document" && "bg-primary/10 text-primary",
                    m.type === "web" && "bg-amber-500/10 text-amber-600 dark:text-amber-400",
                  )}>
                    <Icon size={22} weight="duotone" />
                  </div>
                  <div className="text-sm font-semibold mt-3">{m.name}</div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">{m.type}</div>
                </CardContent></Card>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="classes" className="mt-4">
          <Card className="border-border/60"><CardContent className="p-5">
            <h3 className="font-semibold mb-4" style={{ fontFamily: "Manrope" }}>Running Classes</h3>
            {(c.classes || []).length === 0 ? <p className="text-sm text-muted-foreground">No active classes for this course.</p> :
              <ul className="divide-y divide-border">
                {c.classes.map((cl) => (
                  <li key={cl.id} className="py-3 flex items-center justify-between">
                    <div>
                      <div className="font-semibold text-sm">{cl.name}</div>
                      <div className="text-xs text-muted-foreground">{cl.schedule} · {cl.room}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs font-mono">{cl.student_ids?.length || 0}/{cl.max_capacity || 15}</div>
                      {cl.waiting_list?.length > 0 && (
                        <div className="text-[10px] text-amber-600 dark:text-amber-400 font-semibold">+{cl.waiting_list.length} waitlist</div>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            }
          </CardContent></Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
